import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  GraduationCap,
  Building2,
  Users,
  Heart,
  Shield,
  Home,
  Hotel,
} from "lucide-react";

const PortalSelection = () => {
  const navigate = usePrefixedNavigate();

  const portals = [
    {
      id: "student",
      title: "Student / Parent Portal",
      description:
        "Register as a student or parent to apply for institutions and track applications",
      icon: GraduationCap,
      bgColor: "bg-emerald-600",
      authPath: "/auth?portal=student",
    },
    {
      id: "institution",
      title: "Institution Admin Portal",
      description: "Manage institution operations, applications, and resources",
      icon: Building2,
      bgColor: "bg-purple-600",
      authPath: "/auth?portal=institution",
    },
    {
      id: "trustee",
      title: "Trustee Portal",
      description:
        "Oversee institutional management, compliance, and governance",
      icon: Users,
      bgColor: "bg-amber-600",
      authPath: "/auth?portal=trustee",
    },
    {
      id: "donor",
      title: "Donor Portal",
      description:
        "Support students and institutions through meaningful contributions",
      icon: Heart,
      bgColor: "bg-rose-600",
      authPath: "/auth?portal=donor",
    },
    {
      id: "super_admin",
      title: "Super Admin Portal",
      description:
        "Full system administration, manage all portals, users, and content",
      icon: Shield,
      bgColor: "bg-slate-800",
      authPath: "/auth?portal=super_admin",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
              <Home className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">JBF Portal</h1>
              <p className="text-xs text-muted-foreground">
                Administration System
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h1 className="mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-4xl font-bold text-transparent">
              Select Your Portal
            </h1>
            <p className="text-lg text-muted-foreground">
              Choose the portal that matches your role to continue
            </p>
          </div>

          {/* Hostel Registration CTA */}
          <Card className="mb-8 border-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <CardContent className="py-6">
              <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20">
                    <Hotel className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">
                      Register Your Hostel
                    </h3>
                    <p className="text-blue-100">
                      Join our network of hostels and manage your operations
                      efficiently
                    </p>
                  </div>
                </div>
                <Button
                  size="lg"
                  variant="secondary"
                  className="bg-white text-blue-600 hover:bg-blue-50"
                  onClick={() => navigate("/auth?portal=hostel")}
                >
                  Register Hostel
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {portals.map(portal => {
              const Icon = portal.icon;
              return (
                <Card
                  key={portal.id}
                  className="group border-2 transition-all duration-300 hover:-translate-y-1 hover:border-primary/50 hover:shadow-2xl"
                >
                  <CardHeader className="space-y-4">
                    <div
                      className={`h-20 w-20 rounded-2xl ${portal.bgColor} mb-2 flex items-center justify-center shadow-lg transition-transform group-hover:scale-110`}
                    >
                      <Icon className="h-10 w-10 text-white" />
                    </div>
                    <CardTitle className="text-2xl transition-colors group-hover:text-primary">
                      {portal.title}
                    </CardTitle>
                    <CardDescription className="text-base leading-relaxed">
                      {portal.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      onClick={() => navigate(portal.authPath)}
                      className="w-full shadow-md transition-shadow hover:shadow-lg"
                      size="lg"
                    >
                      Login / Register
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="mt-auto border-t bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-6 text-center text-sm text-muted-foreground">
          © 2024 JBF Portal. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PortalSelection;
